<!-- Record-wide actions stay below the scrolling body. Section actions and
     their forms stay beside the content they affect. -->
<aside class="workspace-page br-compact-page" [class.wd-inline]="inline" [class.workspace-page--full]="!inline" *ngIf="leadId != null"
       aria-label="Lead detail">

  <mat-progress-bar mode="indeterminate" *ngIf="loading"></mat-progress-bar>
  <div class="br-alert br-alert--danger wd-error" *ngIf="loadError"><br-icon>error_outline</br-icon> {{ loadError }}</div>


  <mat-tab-group class="wd-tabs workspace-page__tabs" [mat-stretch-tabs]="false" *ngIf="detail && !loadError"
                 [preserveContent]="false"
                 [selectedIndex]="selectedTab"
                 (selectedIndexChange)="onTabChange($event)">

  <!-- ── What this business is ───────────────────────────────────────────── -->
  <mat-tab label="Info">
  <div class="wd-pane workspace-page" [class.workspace-page--full]="!inline">
    <div class="wd-pane__body wd-info" [class.workspace-page__body]="!inline" [class.workspace-record-grid]="!inline">

      <section class="wd-section" [class.br-card]="!inline" [class.br-p-3]="!inline" *ngIf="recordSection('Lead') as identity">
        <h3 class="br-section-title br-mb-3">{{ identity.title }}</h3>
        <dl class="br-record-rows">
          <ng-container *ngFor="let field of identity.rows">
            <dt>{{ field.label }}</dt>
            <dd [class.br-record-empty]="field.empty">{{ field.value }}</dd>
          </ng-container>
        </dl>
      </section>

      <section class="wd-section" [class.br-card]="!inline" [class.br-p-3]="!inline" [class.workspace-record-grid__tall]="!inline" *ngIf="recordSection('Address') as address">
        <h3 class="br-section-title br-mb-3">{{ address.title }}</h3>
        <dl class="br-record-rows">
          <ng-container *ngFor="let field of address.rows">
            <dt>{{ field.label }}</dt>
            <dd [class.br-record-empty]="field.empty">{{ field.value }}
              <app-map-pin *ngIf="field.location as location"
                           [latitude]="location.latitude" [longitude]="location.longitude"
                           [object]="location.object" [popup]="location.popup"></app-map-pin></dd>
          </ng-container>
        </dl>
        <app-location-preview *ngIf="recordLocation as location"
                              [latitude]="location.latitude" [longitude]="location.longitude"
                              [color]="locationPinColor"></app-location-preview>
      </section>

      <ng-container *ngIf="inline">
        <section class="wd-section" [class.br-card]="!inline" [class.br-p-3]="!inline" *ngIf="recordSection('Next step') as next">
          <h3 class="br-section-title br-mb-3">{{ next.title }}</h3>
          <p class="wd-note">{{ nextActionHeadline }}</p>
          <dl class="br-record-rows">
            <ng-container *ngFor="let field of next.rows">
              <dt>{{ field.label }}</dt><dd [class.br-record-empty]="field.empty">{{ field.value }}</dd>
            </ng-container>
          </dl>
        </section>
        <ng-container [ngTemplateOutlet]="contactsSection"></ng-container>
      </ng-container>

      <!-- What the board judged about it, which is not in the profile: these
           come from the work item, and a rep reads them to decide whether the
           patch is worth the drive. -->
      <ng-template #fitDetails>
      <section class="wd-section" [class.br-card]="!inline" [class.br-p-3]="!inline">
        <h3 class="br-section-title br-mb-3">Fit</h3>
        <dl class="br-record-rows">
          <dt>Score</dt><dd [class.br-record-empty]="detail.item.qualificationScore == null">
            {{ detail.item.qualificationScore ?? '—' }}</dd>
          <dt>Route fit</dt><dd [class.br-record-empty]="!detail.item.routeFitStatus">
            {{ detail.item.routeFitStatus || '—' }}</dd>
        </dl>
      </section>
      </ng-template>
      <ng-container *ngIf="!inline; else fitDetailsDisclosure" [ngTemplateOutlet]="fitDetails"></ng-container>
      <ng-template #fitDetailsDisclosure>
        <details class="wd-disclosure">
          <summary>Qualification</summary>
          <ng-container [ngTemplateOutlet]="fitDetails"></ng-container>
        </details>
      </ng-template>

      <ng-template #recordDetails>
      <section class="wd-section" [class.br-card]="!inline" [class.br-p-3]="!inline" *ngIf="recordSection('Record') as meta">
        <h3 class="br-section-title br-mb-3">{{ meta.title }}</h3>
        <dl class="br-record-rows">
          <ng-container *ngFor="let field of meta.rows">
            <dt>{{ field.label }}</dt>
            <dd [class.br-record-empty]="field.empty">{{ field.value }}</dd>
          </ng-container>
        </dl>
      </section>
      </ng-template>
      <ng-container *ngIf="!inline; else recordDetailsDisclosure" [ngTemplateOutlet]="recordDetails"></ng-container>
      <ng-template #recordDetailsDisclosure>
        <details class="wd-disclosure">
          <summary>Record details</summary>
          <ng-container [ngTemplateOutlet]="recordDetails"></ng-container>
        </details>
      </ng-template>

      <!-- Provenance. Spelled out rather than printed raw: "Matched by name,
           place 41822, 0.87" is a row only the person who wrote the matcher can
           read, and this page is not read by them. -->
      <ng-template #sourceDetails>
      <section class="wd-section workspace-record-grid__wide" [class.br-card]="!inline" [class.br-p-3]="!inline">
        <h3 class="br-section-title br-mb-3">Where it came from <span class="wd-count">{{ detail.sources.length }}</span></h3>
        <p class="wd-note" *ngIf="detail.sources.length">
          This lead was assembled from the source records below. Each line is one record that was
          matched to it, and how sure the match was.
        </p>
        <div class="wd-source" *ngFor="let s of detail.sources">
          <span class="br-badge wd-badge" [class.br-badge--neutral]="s.decision !== 'Accepted'">{{ s.decision }}</span>
          <span class="wd-source-main">Matched on {{ s.matchMethod }}
            <span class="wd-source-ref">{{ s.source }} record {{ s.name || ('#' + s.id) }}</span></span>
          <span class="wd-source-score" *ngIf="s.matchScore != null"
                matTooltip="How confident the match was, 0 to 1">{{ s.matchScore }}</span>
          <span class="wd-source-flag" *ngIf="s.isPrimarySource">the one it was created from</span>
        </div>
        <p class="wd-note" *ngIf="!detail.sources.length">
          Nothing recorded about where this lead came from.
        </p>
      </section>
      </ng-template>
      <ng-container *ngIf="inline" [ngTemplateOutlet]="sourceDetailsDisclosure"></ng-container>
      <ng-template #sourceDetailsDisclosure>
        <details class="wd-disclosure">
          <summary>Sources</summary>
          <ng-container [ngTemplateOutlet]="sourceDetails"></ng-container>
        </details>
      </ng-template>





      <!-- Two sections, because they answer two questions. They were one,
           called "What it is worth", that also held the next action - which is
           not worth anything, the same conflation the record's own "Pipeline"
           grouping had.

           The heading is the record's, like every other section on this tab.
           It was still the merged section's old wording, hard-coded, so this
           box was called "What it is worth" here and "Value" on the form that
           edits it: one heading in five that ignored the title the record
           gave it, and the only one where reading and editing disagreed. -->
      <ng-template #valueDetails>
      <section class="wd-section" [class.br-card]="!inline" [class.br-p-3]="!inline" *ngIf="recordSection('Value') as value">
        <h3 class="br-section-title br-mb-3">{{ value.title }}</h3>
        <dl class="br-record-rows">
          <ng-container *ngFor="let field of value.rows">
            <dt>{{ field.label }}</dt>
            <dd [class.br-record-empty]="field.empty">{{ field.value }}</dd>
          </ng-container>
        </dl>
      </section>
      </ng-template>
      <ng-container *ngIf="!inline; else valueDetailsDisclosure" [ngTemplateOutlet]="valueDetails"></ng-container>
      <ng-template #valueDetailsDisclosure>
        <details class="wd-disclosure">
          <summary>Value</summary>
          <ng-container [ngTemplateOutlet]="valueDetails"></ng-container>
        </details>
      </ng-template>



      <ng-container *ngIf="!inline" [ngTemplateOutlet]="sourceDetails"></ng-container>

      <ng-template #contactsSection>
      <!-- People at this lead. Pre-Won contacts are their own records; a
           suppressed one stays visible but is never contacted again. -->
      <section class="wd-section workspace-record-grid__wide" [class.br-card]="!inline" [class.br-p-3]="!inline">
        <div class="br-flex br-items-center br-justify-between br-flex-wrap br-gap-2 br-mb-3">
          <h3 class="br-section-title br-mb-0">Contacts <span class="wd-count">{{ detail.contacts.length }}</span></h3>
          <button type="button" class="br-btn br-btn--primary br-btn--sm"
                  *ngIf="canEditEngagement && !showContactForm" (click)="showContactForm = true">
            <br-icon>person_add</br-icon> Add contact
          </button>
        </div>

        <div class="wd-form" *ngIf="canEditEngagement && showContactForm">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Name</mat-label><input matInput [(ngModel)]="contactDraft.displayName" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Title</mat-label><input matInput [(ngModel)]="contactDraft.title" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Phone</mat-label><input matInput [(ngModel)]="contactDraft.phone" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Email</mat-label><input matInput [(ngModel)]="contactDraft.email" />
          </mat-form-field>
          <mat-checkbox [(ngModel)]="contactDraft.isPrimary">Primary contact</mat-checkbox>
          <div class="wd-form-actions">
            <button type="button" class="br-btn br-btn--primary br-btn--sm"
                    [disabled]="!contactDraftValid || engagementBusy" (click)="addContact()">
              {{ engagementBusy ? 'Saving…' : 'Save contact' }}</button>
            <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
                    (click)="showContactForm = false">Cancel</button>
          </div>
        </div>

        <div class="wd-contact" *ngFor="let c of detail.contacts" [class.wd-suppressed]="c.suppressed">
          <div class="wd-contact-head">
            <span class="wd-contact-name">{{ contactLabel(c) }}</span>
            <span class="br-badge wd-chip-primary" *ngIf="c.isPrimary">primary</span>
            <span class="br-badge wd-chip-off" *ngIf="c.suppressed">suppressed</span>
            <span class="br-badge br-badge--neutral" *ngIf="!c.verifiedAt">unverified</span>
            <button mat-icon-button class="wd-suppress" *ngIf="canEditEngagement && !c.suppressed"
                    [disabled]="engagementBusy" (click)="suppressContact(c)"
                    matTooltip="Suppress: keeps the record, stops contact">
              <br-icon>block</br-icon>
            </button>
          </div>
          <div class="wd-contact-meta">
            <span *ngIf="c.title">{{ c.title }}</span>
            <ng-container *ngIf="inline; else contactText">
              <a *ngIf="c.phone && !c.suppressed" [href]="'tel:' + c.phone">{{ c.phone }}</a>
              <a *ngIf="c.email && !c.suppressed" [href]="'mailto:' + c.email">{{ c.email }}</a>
              <ng-container *ngIf="c.suppressed" [ngTemplateOutlet]="contactText"></ng-container>
            </ng-container>
            <ng-template #contactText>
              <span *ngIf="c.phone">{{ c.phone }}</span>
              <span *ngIf="c.email">{{ c.email }}</span>
            </ng-template>
          </div>
          <div class="wd-contact-meta">
            <span>added by {{ c.source }}</span>
            <span *ngIf="c.confidence != null">confidence {{ c.confidence }}</span>
            <span class="wd-suppressed-why" *ngIf="c.suppressedReason">{{ c.suppressedReason }}</span>
          </div>
        </div>
        <p class="wd-note" *ngIf="!detail.contacts.length">
          Nobody to call yet.
        </p>

      </section>

      </ng-template>
      <ng-container *ngIf="!inline" [ngTemplateOutlet]="contactsSection"></ng-container>

      <!-- Post-promotion hand-off to CRM. -->
      <section class="wd-section workspace-record-grid__wide" [class.br-card]="!inline" [class.br-p-3]="!inline" *ngIf="detail.convertedCustomerId != null">
        <h3 class="br-section-title br-mb-3">Customer account</h3>
        <p class="wd-note">
          Won on {{ detail.convertedAt | date:'MMM d, y' }}. Billing and contracts live on the
          customer account from here.
        </p>
      </section>

    </div>

    <div class="wd-pane__actions workspace-page__footer" *ngIf="!inline || detail.convertedCustomerId != null || !canEditEngagement">
      <button class="br-btn br-btn--primary br-btn--sm" *ngIf="!inline && canEditEngagement"
              (click)="editRequested.emit()">
        <br-icon>edit</br-icon> Edit
      </button>
      <button class="br-btn br-btn--outline-secondary br-btn--sm"
              *ngIf="detail.convertedCustomerId != null" (click)="openCustomer()">
        <br-icon>open_in_new</br-icon> Open the customer account
      </button>
      <span class="wd-note" *ngIf="!canEditEngagement">Read-only for your role.</span>
    </div>
  </div>
  </mat-tab>

  <!-- ── Where the deal stands ───────────────────────────────────────────── -->
  <!-- Deal and People were two tabs. They are one working view: where the
       deal stands, what it is worth, who owns it and who to call. The
       sections keep their own headings, so nothing lost a name by merging. -->
  <!-- ── What has happened ───────────────────────────────────────────────── -->
  <mat-tab label="Activity">
  <div class="wd-pane workspace-page" [class.workspace-page--full]="!inline">
    <div class="wd-pane__body" [class.workspace-page__body]="!inline" [class.workspace-record-grid]="!inline">


      <section class="wd-section wd-schedule" [class.br-card]="!inline" [class.br-p-3]="!inline" *ngIf="nextAction || recordSection('Next step') || canEditCase || canPlanVisit || scheduleSaved">
        <div class="br-flex br-items-center br-justify-between br-flex-wrap br-gap-2 br-mb-3">
          <h3 class="br-section-title br-mb-0">Next step</h3>
          <button type="button" class="br-btn br-btn--outline-secondary br-btn--sm"
                  *ngIf="canPlanVisit" [disabled]="visitNavigationBlocked" (click)="requestVisit()"
                  [matTooltip]="visitNavigationBlocked ? 'Finish or cancel the open form before planning a visit.' : 'Choose a date and route for an on-site visit.'">
            <br-icon>alt_route</br-icon> Plan a visit
          </button>
          <button type="button" class="br-btn br-btn--primary br-btn--sm"
                  *ngIf="canEditCase && !scheduling" (click)="openSchedule()">
            <br-icon>event</br-icon> Schedule event
          </button>
        </div>
        <!-- What it is waiting for, first: it is the question the record is
             opened to answer, and a due date printed as a field leaves the
             reader to work out that it went by nine days ago. -->
        <section class="wd-next" *ngIf="nextAction"
                 [class.wd-next--overdue]="nextAction.state === 'overdue'"
                 [class.wd-next--today]="nextAction.state === 'today'"
                 [class.wd-next--none]="nextAction.state === 'none'">
          <br-icon class="wd-next__icon">{{
            nextAction.state === 'overdue' ? 'error_outline'
              : nextAction.state === 'today' ? 'today'
              : nextAction.state === 'none' ? 'event_busy' : 'event_available' }}</br-icon>
          <div>
            <div class="wd-next__headline">{{ nextActionHeadline }}</div>
            <div class="wd-next__detail" *ngIf="nextAction.due">Due {{ nextAction.due }}</div>
          </div>
        </section>

        <dl class="br-record-rows" *ngIf="recordSection('Next step') as next">
          <ng-container *ngFor="let field of next.rows">
            <dt>{{ field.label }}</dt>
            <dd [class.br-record-empty]="field.empty">{{ field.value }}</dd>
          </ng-container>
        </dl>
        <div class="br-alert br-alert--success wd-scheduled" *ngIf="scheduleSaved && !scheduling">
          <br-icon>event_available</br-icon> {{ scheduleSaved }}
        </div>
        <div class="wd-form" *ngIf="scheduling">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>What</mat-label>
            <input matInput [(ngModel)]="nextActionDraft" placeholder="Site visit" />
          </mat-form-field>
          <!-- Both ways in: type the segments, or press the button for the
               picker. A bare <input type="datetime-local"> offers only the
               first here - the Material outline field clips the browser's own
               indicator, which is why app-date-field exists at all.

               With a time, because a visit booked for "Thursday" with no hour
               is how two reps arrive at once. -->
          <app-date-field label="When" mode="datetime"
                          [(ngModel)]="nextActionDueDraft"></app-date-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Where</mat-label>
            <input matInput [(ngModel)]="nextActionPlaceDraft"
                   placeholder="The lead's address, a room, a phone number" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Memo</mat-label>
            <textarea matInput rows="2" [(ngModel)]="nextActionNoteDraft"
                      placeholder="What to bring or remember"></textarea>
          </mat-form-field>
          <div class="wd-form-actions">
            <button class="br-btn br-btn--primary br-btn--sm"
                    [disabled]="!scheduleDirty || saving" (click)="saveSchedule()">
              {{ saving ? 'Saving…' : 'Schedule' }}</button>
            <button class="br-btn br-btn--outline-secondary br-btn--sm"
                    (click)="cancelSchedule()">Cancel</button>
          </div>
          <div class="br-alert br-alert--danger wd-error" *ngIf="saveError"><br-icon>block</br-icon> {{ saveError }}</div>
        </div>
      </section>

      <!-- Writing to the lead. Sending remains an explicit action: composing
           text never records an activity or puts a message on the wire. -->
      <section class="wd-section wd-email" [class.br-card]="!inline" [class.br-p-3]="!inline" *ngIf="canEditEngagement || emailing">
        <div class="br-flex br-items-center br-justify-between br-flex-wrap br-gap-2 br-mb-3">
          <h3 class="br-section-title br-mb-0">Email this lead</h3>
          <button type="button" class="br-btn br-btn--primary br-btn--sm"
                  *ngIf="canEditEngagement && !emailing" (click)="openEmail()">
            <br-icon>mail</br-icon> Email
          </button>
        </div>
        <div class="wd-form" *ngIf="emailing">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>To</mat-label>
            <input matInput [(ngModel)]="emailDraft.to" [matAutocomplete]="toList" />
            <mat-autocomplete #toList="matAutocomplete">
              <mat-option *ngFor="let address of contactEmails" [value]="address">
                {{ address }}</mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Subject</mat-label>
            <input matInput [(ngModel)]="emailDraft.subject" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Message</mat-label>
            <textarea matInput rows="8" [(ngModel)]="emailDraft.body"></textarea>
          </mat-form-field>

          <div class="wd-form-actions">
            <button class="br-btn br-btn--primary br-btn--sm"
                    [disabled]="!emailValid || emailBusy" (click)="sendEmail()">
              {{ emailBusy ? 'Sendingâ€¦' : 'Send' }}</button>
            <button class="br-btn br-btn--outline-secondary br-btn--sm"
                    (click)="cancelEmail()">Cancel</button>
            <span class="wd-note">Replies come back to you.</span>
          </div>
          <div class="br-alert br-alert--danger wd-error" *ngIf="emailError">
            <br-icon>block</br-icon> {{ emailError }}
          </div>
        </div>
      </section>
      <!-- Human and agent entries side by side, never blurred - the actor is
           the difference between "we called" and "the agent drafted". -->
      <section class="wd-section wd-activity-section workspace-record-grid__wide" [class.br-card]="!inline" [class.br-p-3]="!inline">
        <div class="br-flex br-items-center br-justify-between br-flex-wrap br-gap-2 br-mb-3">
          <h3 class="br-section-title br-mb-0">Activity <span class="wd-count">{{ detail.activities.length }}</span></h3>
          <button type="button" class="br-btn br-btn--primary br-btn--sm"
                  *ngIf="canEditEngagement && !showActivityForm" (click)="openActivity()">
            <br-icon>post_add</br-icon> Log activity
          </button>
        </div>

        <div class="wd-form" *ngIf="canEditEngagement && showActivityForm">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Type</mat-label>
            <mat-select [(ngModel)]="activityDraft.activityType">
              <mat-option *ngFor="let t of activityTypes" [value]="t">{{ t }}</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Subject</mat-label><input matInput [(ngModel)]="activityDraft.subject" />
          </mat-form-field>
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Notes</mat-label><textarea matInput rows="2" [(ngModel)]="activityDraft.body"></textarea>
          </mat-form-field>
          <div class="wd-form-actions">
            <button class="br-btn br-btn--primary br-btn--sm"
                    [disabled]="!activityDraftValid || engagementBusy" (click)="addActivity()">
              {{ engagementBusy ? 'Saving…' : 'Log activity' }}</button>
            <button class="br-btn br-btn--outline-secondary br-btn--sm"
                    (click)="cancelActivity()">Cancel</button>
          </div>
        </div>

        <div class="wd-activity" *ngFor="let a of detail.activities" [class.wd-by-agent]="isAgentEntry(a)">
          <div class="wd-activity-head">
            <span class="br-badge br-badge--neutral" [class.br-badge--ai]="isAgentEntry(a)">{{ a.activityType }}</span>
            <span class="wd-activity-subject">{{ a.subject }}</span>
            <span class="wd-activity-when">{{ a.occurredAt | date:'MMM d' }}</span>
          </div>
          <div class="wd-activity-meta">
            {{ activityActor(a) }}
          </div>
          <div class="wd-activity-body" *ngIf="a.body">{{ a.body }}</div>
        </div>
        <p class="wd-note" *ngIf="!detail.activities.length">
          Nothing logged against this lead yet.
        </p>
        <p class="wd-note" *ngIf="!canEditEngagement">Read-only for your role.</p>
        <div class="br-alert br-alert--danger wd-error" *ngIf="engagementError"><br-icon>block</br-icon> {{ engagementError }}</div>
      </section>

    </div>


  </div>
  </mat-tab>

  <!-- ── Paperwork ───────────────────────────────────────────────────────
       Quotes, not the customer's contracts. A contract needs a customer
       account and a lead has none until a quote is accepted - the account is
       created inside that transaction. This tab used to explain that and
       offer nothing, while the quote API it should have been driving had no
       screen anywhere in the product. -->
  <mat-tab label="Quotes">
  <div class="wd-pane workspace-page" [class.workspace-page--full]="!inline">
    <div class="wd-pane__body" [class.workspace-page__body]="!inline" [class.workspace-record-grid]="!inline">

      <app-lead-quotes class="workspace-record-grid__wide" [class.br-card]="!inline" [class.br-p-3]="!inline"
                       [leadId]="leadId"
                       [canManage]="canManagePipeline"
                       [canApprove]="canApproveQuotes"
                       (changed)="onQuoteAccepted()">
      </app-lead-quotes>
    </div>

    <div class="wd-pane__actions workspace-page__footer" *ngIf="customerId != null">
      <button class="br-btn br-btn--outline-secondary br-btn--sm"
              (click)="openCustomer()">
        <br-icon>open_in_new</br-icon> The customer account
      </button>
    </div>
  </div>
  </mat-tab>

</mat-tab-group>
</aside>
